/**
 * The provider-neutral delivery states AidKit exposes to staff.
 *
 * Provider APIs have more granular, provider-specific vocabularies (for example Twilio's
 * `accepted` and SES's `DeliveryDelay`). Those values must be normalized before they reach an
 * Admin Panel delivery-status field or aggregate.
 */
export const DELIVERY_STATUSES = [
  // Waiting on a future send time (Twilio SMS/WhatsApp only, up to 35 days).
  'scheduled',
  'queued',
  'sent',
  'delivered',
  'read',
  'undelivered',
  'failed',
] as const;

export type DeliveryStatus = typeof DELIVERY_STATUSES[number];

/**
 * Twilio callback statuses (lowercase) and the delivery status each translates to. Every
 * DeliveryStatus maps to itself, so this also covers values already normalized at write time.
 *
 * Add new statuses here with a test when Twilio documents or sends one.
 */
export const TWILIO_STATUS_TO_DELIVERY_STATUS: Readonly<Record<string, DeliveryStatus>> = {
  // Twilio sends no callback while a message is scheduled, so this only guards against one appearing.
  scheduled: 'scheduled',
  accepted: 'queued',
  queued: 'queued',
  sending: 'queued',
  resending: 'queued',
  sent: 'sent',
  delivered: 'delivered',
  // Twilio reports this for WhatsApp after the recipient opens the message.
  read: 'read',
  undelivered: 'undelivered',
  failed: 'failed',
  canceled: 'failed',
};

/**
 * Translate a Twilio callback status into AidKit's provider-neutral delivery vocabulary.
 *
 * Returning null is intentional: a callback with a provider status we do not understand is not
 * a valid delivery update, so it must not overwrite the last known status.
 */
export function normalizeTwilioStatus(status: string): DeliveryStatus | null {
  const key = status.trim().toLowerCase();
  // Own-property check so prototype keys like 'constructor' stay unrecognized.
  return Object.prototype.hasOwnProperty.call(TWILIO_STATUS_TO_DELIVERY_STATUS, key)
    ? TWILIO_STATUS_TO_DELIVERY_STATUS[key]
    : null;
}
